import axios from "axios";
import { createContext } from "react";

const AgentContext = createContext();

export const AgentContextProvider = ({ children }) => {
  // Installable templates: the PUBLISHED templates of apps this account has installed —
  // the only template surface a regular user sees. Template management is a sysadmin feature.
  const getInstallableTemplates = () => axios.get("/api/agent/installable-templates");
  const getAgentModels = () => axios.get("/api/agent/models");

  // Customer agents (what runs actually target)
  const getAgents = (type) => axios.get("/api/agent/agents" + (type ? `?type=${type}` : ""));
  const getAgent = (id) => axios.get(`/api/agent/agents/${id}`);
  const createAgent = (data) => axios.post("/api/agent/agents", data);
  const updateAgent = (id, data) => axios.put(`/api/agent/agents/${id}`, data);
  const publishAgent = (id) => axios.post(`/api/agent/agents/${id}/publish`);
  const getAgentVersions = (id) => axios.get(`/api/agent/agents/${id}/versions`);
  const deleteAgent = (id) => axios.delete(`/api/agent/agents/${id}`);
  const unsuspendAgent = (id) => axios.post(`/api/agent/agents/${id}/unsuspend`);

  // Runs (test console)
  const startAgentRun = (data) => axios.post("/api/agent/runs", data);
  const getAgentRun = (id) => axios.get("/api/agent/runs/" + id);
  const getAgentRuns = () => axios.get("/api/agent/runs");
  const getAgentRunsByParent = (parentRunId) =>
    axios.get(`/api/agent/runs?parentRunId=${parentRunId}`);
  const getAgentRunEvents = (id, after = 0, limit = 100) =>
    axios.get(`/api/agent/runs/${id}/events?after=${after}&limit=${limit}`);
  const getAgentRunResult = (id) => axios.get(`/api/agent/runs/${id}/result`);
  const resolveAgentInteraction = (runId, interactionId, data) =>
    axios.post(`/api/agent/runs/${runId}/interactions/${interactionId}`, data);
  const resolveAgentClientTool = (runId, callId, data) =>
    axios.post(`/api/agent/runs/${runId}/client-tools/${callId}`, data);
  // Dereference offloaded (>8KB) arguments — the run only carries an artifact pointer.
  const getAgentRunClientToolArguments = (runId, callId) =>
    axios.get(`/api/agent/runs/${runId}/client-tools/${callId}/arguments`);
  const getAgentRunInteractionArguments = (runId, interactionId) =>
    axios.get(`/api/agent/runs/${runId}/interactions/${interactionId}/arguments`);
  // Test-console inspection (Portal-only endpoints): the run's raw model history and the
  // full artifact behind a truncated event payload preview.
  const getAgentRunHistory = (runId) => axios.get(`/api/agent/runs/${runId}/history`);
  const getAgentRunEventPayload = (runId, seq) =>
    axios.get(`/api/agent/runs/${runId}/events/${seq}/payload`);
  const cancelAgentRun = (id) => axios.post(`/api/agent/runs/${id}/cancel`);

  // Conversations (multi-turn threads)
  const getAgentConversations = () => axios.get("/api/agent/conversations");
  const getAgentConversation = (id) => axios.get(`/api/agent/conversations/${id}`);
  const continueAgentConversation = (id, data) =>
    axios.post(`/api/agent/conversations/${id}/runs`, data);
  const createAgentConversation = (data) => axios.post("/api/agent/conversations", data);

  // Conversation files (attach-files flow)
  const uploadAgentFile = (conversationId, file, onUploadProgress) => {
    const form = new FormData();
    form.append("file", file);
    return axios.post(`/api/agent/conversations/${conversationId}/files`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    });
  };
  const getAgentFiles = (conversationId) =>
    axios.get(`/api/agent/conversations/${conversationId}/files`);
  // Downloads must go through axios — the request interceptor injects the accountId
  // header, which a plain <a href> navigation would miss (the server 401s without it).
  const downloadAgentFile = async (fileId, fileName) => {
    const response = await axios.get(`/api/agent/files/${fileId}/content`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <AgentContext.Provider
      value={{
        getInstallableTemplates,
        getAgentModels,
        getAgents,
        getAgent,
        createAgent,
        updateAgent,
        publishAgent,
        getAgentVersions,
        deleteAgent,
        unsuspendAgent,
        startAgentRun,
        getAgentRun,
        getAgentRuns,
        getAgentRunsByParent,
        getAgentRunEvents,
        getAgentRunResult,
        resolveAgentInteraction,
        resolveAgentClientTool,
        getAgentRunClientToolArguments,
        getAgentRunInteractionArguments,
        getAgentRunHistory,
        getAgentRunEventPayload,
        cancelAgentRun,
        getAgentConversations,
        getAgentConversation,
        continueAgentConversation,
        createAgentConversation,
        uploadAgentFile,
        getAgentFiles,
        downloadAgentFile,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};

export default AgentContext;
